import { z } from 'zod';

import { baseEntitySchema } from './baseContract';
import { ChatRole, MessageGenerationStatus, MessageStopReason } from '../enums';

export const uiActionSchema = z.enum([
  'find_similar_products',
  'show_more_products',
  'continue_response',
]);

export type UiAction = z.infer<typeof uiActionSchema>;

export function uiActionOf(message: {
  metadata?: Record<string, unknown> | null;
}): UiAction | undefined {
  if (message.metadata?.origin !== 'ui_action') return undefined;
  const action = uiActionSchema.safeParse(message.metadata.action);

  return action.success ? action.data : undefined;
}

export function formatUiActionMessage(text: string, action?: UiAction): string {
  return action
    ? `[UI action: ${action}; automatically generated button text, not a customer-authored language preference]\n${text}`
    : text;
}

// Base ChatMessage schema
export const chatMessageSchema = baseEntitySchema.extend({
  thread: z.string().uuid(),
  feedback: z.string().uuid().optional(),
  role: z.nativeEnum(ChatRole),
  turnId: z.string().uuid().nullable().optional(),
  generationStatus: z.nativeEnum(MessageGenerationStatus).nullable().optional(),
  stopReason: z.nativeEnum(MessageStopReason).nullable().optional(),
  content: z.string().optional(),
  user: z.string().uuid().optional(),
  metadata: z.record(z.any()).optional(),
  toolCalls: z.array(z.any()).optional(),
});

// Create DTO schema
export const createChatMessageSchema = chatMessageSchema
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .strict();

// Visitor-authored history includes persisted FAQ/campaign assistant text.
// Operator identity, tool state, and generation bookkeeping are server-owned.
export const visitorChatMessageSchema = z
  .object({
    role: z.enum([ChatRole.User, ChatRole.Assistant]),
    content: z.string(),
  })
  .strict();

export const createVisitorChatMessagesSchema = z
  .object({ messages: z.array(visitorChatMessageSchema).min(1) })
  .strict();

// Update DTO schema
export const updateChatMessageSchema = chatMessageSchema
  .partial()
  .required({
    id: true,
  })
  .strict();

export type ChatMessageDTO = z.infer<typeof chatMessageSchema>;
export type CreateChatMessageDTO = z.infer<typeof createChatMessageSchema>;
export type VisitorChatMessageDTO = z.infer<typeof visitorChatMessageSchema>;
export type UpdateChatMessageDTO = z.infer<typeof updateChatMessageSchema>;
